import React, { useState } from 'react';
import { Download, CheckCircle, Database, Server, Code, FileJson, Loader2 } from 'lucide-react';

interface IntegrationsDashboardProps {
  totalClientsCount: number;
  clientsLength: number;
  isExportingJson: boolean;
  onExportJson: () => void;
}

export const IntegrationsDashboard: React.FC<IntegrationsDashboardProps> = ({
  totalClientsCount,
  clientsLength,
  isExportingJson,
  onExportJson
}) => {
  const [activeTab, setActiveTab] = useState<'export' | 'api' | 'import'>('export');
  const apiUrl = `${window.location.origin}/api/clients-directory`;
  const importApiUrl = `${window.location.origin}/api/importar-site`;

  return (
    <div className="max-w-4xl mx-auto mt-6">
      
      <div className="flex border-b border-gray-200 mb-6 gap-6">
        <button 
          onClick={() => setActiveTab('export')}
          className={`pb-3 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'export' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-900 border-b-transparent'}`}
        >
          <FileJson size={16} /> Exportação de Dados
        </button>
        <button 
          onClick={() => setActiveTab('api')}
          className={`pb-3 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'api' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-900 border-b-transparent'}`}
        >
          <Server size={16} /> API de Clientes
        </button>
        <button 
          onClick={() => setActiveTab('import')}
          className={`pb-3 text-sm font-medium transition-colors border-b-2 flex items-center gap-2 ${activeTab === 'import' ? 'border-black text-black' : 'border-transparent text-gray-500 hover:text-gray-900 border-b-transparent'}`}
        >
          <Database size={16} /> Importação de Site
        </button>
      </div>

      {activeTab === 'export' && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-green-50 text-green-600 rounded-xl flex items-center justify-center shrink-0">
              <Download size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Exportar Leads (JSON)</h2>
              <p className="text-sm text-gray-500 mt-1">Gere um arquivo JSON com os dados de todos os clientes cadastrados para alimentar o seu outro sistema de captação.</p>
            </div>
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-5 mb-8">
            <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2"><CheckCircle size={16} className="text-green-600" /> O que será exportado:</h3>
            <ul className="text-sm text-gray-600 space-y-2 list-disc list-inside">
              <li><strong>ID Único:</strong> Identificador interno para o seu sistema evitar duplicidade.</li>
              <li><strong>Nome/Título:</strong> Nome do estabelecimento comercial.</li>
              <li><strong>Telefone:</strong> Número de whatsapp ou contato.</li>
              <li><strong>Link do Maps:</strong> Link direto para o Google Maps.</li>
              <li><strong>Nicho:</strong> Categoria principal do projeto.</li>
              <li className="list-none pt-2 font-medium text-gray-800">
                Total atual: {totalClientsCount > 0 ? totalClientsCount : clientsLength} leads encontrados no banco de dados.
              </li>
            </ul>
          </div>

          <div className="flex items-center justify-between border-t border-gray-100 pt-6">
            <div className="text-sm text-gray-500">
              O arquivo será baixado imediatamente no formato <code>.json</code> e não exibe limites de quantidade.
            </div>
            <button 
              onClick={onExportJson}
              disabled={isExportingJson}
              className="bg-black hover:bg-gray-900 text-white px-6 py-2.5 rounded-lg text-sm font-medium transition-all shadow-md flex items-center gap-2 disabled:bg-gray-400 disabled:cursor-not-allowed"
            >
              {isExportingJson ? <Loader2 size={18} className="animate-spin" /> : <Download size={18} />}
              {isExportingJson ? "Baixando..." : `Baixar Todos os Leads`}
            </button>
          </div>
        </div>
      )}

      {activeTab === 'api' && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-blue-50 text-blue-600 rounded-xl flex items-center justify-center shrink-0">
              <Database size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Endpoint da API: Listagem de Clientes</h2>
              <p className="text-sm text-gray-500 mt-1">Conecte outros sistemas, bots ou planilhas à nossa base de clientes através desta REST API.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">Método e URL</label>
              <div className="flex items-center border border-gray-200 rounded-lg bg-gray-50 overflow-hidden mb-4">
                <span className="px-4 py-3 bg-blue-100 text-blue-700 font-bold text-sm border-r border-gray-200">GET</span>
                <input 
                  type="text" 
                  readOnly 
                  value={apiUrl}
                  className="w-full bg-transparent px-4 py-3 text-sm font-mono text-gray-700 outline-none"
                />
              </div>
              
              <label className="block text-sm font-bold text-gray-900 mb-2 mt-4">Chave de Segurança (Header)</label>
              <div className="flex flex-col gap-2">
                <p className="text-xs text-gray-600 mb-1">Para acessar os dados de outro sistema (Zapier, Make, n8n, etc), você precisa enviar este cabeçalho (Header) na requisição HTTP: Cada parceiro recebe um token próprio, com escopo e validade, emitido pelo servidor. Ele é mostrado UMA vez no momento da emissão e guardado como hash — revogar um parceiro passa a ser um UPDATE, não um deploy.</p>
                <div className="flex items-center border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                  <span className="px-4 py-3 bg-gray-200 text-gray-700 font-bold text-sm border-r border-gray-300 w-36 whitespace-nowrap">Authorization</span>
                  <input 
                    type="text" 
                    readOnly 
                    value="Bearer <token do parceiro — gerado no servidor>"
                    className="w-full bg-transparent px-4 py-3 text-sm font-mono text-gray-800 outline-none select-all"
                  />
                </div>
              </div>
            </div>

            <div>
               <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 mt-6"><Code size={16} className="text-gray-500" /> Exemplo de Resposta (JSON)</h3>
               <pre className="bg-gray-900 text-green-400 p-4 rounded-lg text-sm font-mono overflow-x-auto shadow-inner">
{`[
  {
    "id": "123e4567-e89b-12d3...",
    "name": "Padaria Dig Pão",
    "phone": "31999999999",
    "niche": "bakery",
    "status": "active"
  },
  ...
]`}
               </pre>
            </div>

            <div className="bg-blue-50 border border-blue-100 p-4 rounded-lg flex items-start gap-3 mt-4 text-blue-800 text-sm">
              <div className="mt-0.5">ℹ️</div>
              <p>Esta API é protegida pela chave de segurança acima, e pode ser conectada de qualquer lugar (N8N, Make, Zapier, Google Sheets) via requisição HTTP GET usando o header de autorização. Ela busca os dados em tempo real diretamente do banco oficial.</p>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'import' && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 shadow-sm">
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 bg-purple-50 text-purple-600 rounded-xl flex items-center justify-center shrink-0">
              <Database size={24} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900">Webhook de Importação de Site</h2>
              <p className="text-sm text-gray-500 mt-1">Endpoint de recebimento direto (Server-to-Server) dos sites recém criados, substituindo a necessidade do antigo Supabase ou geração de ZIPs.</p>
            </div>
          </div>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-bold text-gray-900 mb-2">Método e Endpoint de Recebimento</label>
              <div className="flex flex-col gap-2">
                <p className="text-xs text-gray-600 mb-1">Adicione esta URL no seu gerador de sites para que ele envie os dados assim que terminar de criar.</p>
                <div className="flex items-center border border-gray-200 rounded-lg bg-gray-50 overflow-hidden mb-4">
                  <span className="px-4 py-3 bg-purple-100 text-purple-700 font-bold text-sm border-r border-gray-200">POST</span>
                  <input 
                    type="text" 
                    readOnly 
                    value={importApiUrl}
                    className="w-full bg-transparent px-4 py-3 text-sm font-mono text-gray-700 outline-none select-all"
                  />
                </div>
              </div>
              
              <label className="block text-sm font-bold text-gray-900 mb-2 mt-4">Chave de Segurança (Header)</label>
              <div className="flex flex-col gap-2">
                <p className="text-xs text-gray-600 mb-1">Proteção contra spam. O gerador deve enviar o mesmo header de segurança:</p>
                <div className="flex items-center border border-gray-200 rounded-lg bg-gray-50 overflow-hidden">
                  <span className="px-4 py-3 bg-gray-200 text-gray-700 font-bold text-sm border-r border-gray-300 w-36 whitespace-nowrap">Authorization</span>
                  <input 
                    type="text" 
                    readOnly 
                    value="Bearer <token do parceiro — gerado no servidor>"
                    className="w-full bg-transparent px-4 py-3 text-sm font-mono text-gray-800 outline-none select-all"
                  />
                </div>
              </div>
            </div>

            <div>
               <h3 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2 mt-6"><Code size={16} className="text-gray-500" /> Estrutura do Payload (JSON)</h3>
               <p className="text-xs text-gray-500 mb-2">O seu gerador deve enviar no Body (Raw JSON) os três arquivos base:</p>
               <pre className="bg-gray-900 text-purple-400 p-4 rounded-lg text-sm font-mono overflow-x-auto shadow-inner">
{`{
  "clientName": "Padaria Dig Pão",
  "clientPhone": "31999999999",
  "niche": "bakery",
  "siteData": {
    "indexHtml": "<!DOCTYPE html><html>...",
    "dataJson": { "colors": {...}, "texts": {...} },
    "systemMetaJson": { "version": "1.0", "template": "standard" }
  }
}`}
               </pre>
            </div>

            <div className="bg-purple-50 border border-purple-100 p-4 rounded-lg flex items-start gap-3 mt-4 text-purple-800 text-sm">
              <div className="mt-0.5">💡</div>
              <div>
                <strong>Como funciona na prática:</strong>
                <p className="mt-1">Ao invés do seu gerador criar um ZIP (<code>data.json, index.html, system_meta.json</code>) e pedir para você fazer upload, ele empacota o conteúdo desses arquivos em um único Payload (como acima) e envia para a URL desta página via requisição HTTP.</p>
                <p className="mt-2">Nosso sistema recebe, cria o cliente automaticamente, salva os leads e já prepara o Editor Visual, tudo em menos de 1 segundo.</p>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};
